import { GraphQLErrorPolicies } from 'shared/enums/GraphQLErrorPolicies';
import { useDuplicateOneAutomationMutation } from 'shared/types/graphql';
import { useHistory } from 'react-router-dom';
import { useToast } from '@televet/kibble-ui/build/components/Toast';
import { getAutomationsListQuery, getGlobalAutomationsListQuery } from '../constants/operationNames';
import { useAppSelector } from 'state/hooks';
import { useGetAutomationsBasePath } from './useGetAutomationsBasePath';

interface useDuplicateOneAutomationProps {
  id: string;
}

export const useDuplicateOneAutomation = ({
  id,
}: useDuplicateOneAutomationProps): { handleDuplicateAutomation: () => Promise<void> } => {
  const history = useHistory();
  const toast = useToast();
  const isGlobalEditor = useAppSelector((state) => state.automations.isGlobalEditor);
  const { automationsBasePath } = useGetAutomationsBasePath();

  const [duplicateOneAutomation] = useDuplicateOneAutomationMutation({
    variables: { where: { id } },
    errorPolicy: GraphQLErrorPolicies.All,
    onCompleted: (response) => {
      const newAutomationId = response?.copyOneWorkflowEventSetting?.id;
      history.push(`${automationsBasePath}/${newAutomationId}`);
    },
    awaitRefetchQueries: true,
    refetchQueries: isGlobalEditor ? [getGlobalAutomationsListQuery] : [getAutomationsListQuery],
  });

  const handleDuplicateAutomation = async (): Promise<void> => {
    try {
      await duplicateOneAutomation();
      toast({
        status: 'success',
        title: 'Success!',
        description: 'Automation duplicated successfully.',
      });
    } catch (error) {
      toast({
        status: 'error',
        title: 'Error!',
        description: 'Unable to duplicate automation.',
      });
    }
  };

  return { handleDuplicateAutomation };
};
